import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const getNavHeights = (shrunk) => {
  const w = window.innerWidth;
  if (w >= 768) return { bar: shrunk ? 64 : 96, logo: shrunk ? 56 : 96 };
  if (w >= 640) return { bar: shrunk ? 56 : 80, logo: shrunk ? 48 : 64 };
  return { bar: shrunk ? 48 : 64, logo: shrunk ? 40 : 48 };
};

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const Navbar = ({ activeLink = 'home' }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const dropdownRef = useRef(null);
  const spacerRef = useRef(null);
  const navInnerRef = useRef(null);
  const logoRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenDropdown(null);
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    const spacer = spacerRef.current;
    const inner = navInnerRef.current;
    const logo = logoRef.current;
    if (!spacer || !inner || !logo) return;

    let frameId = null;
    let curBar, curLogo;

    const setHeights = (bar, lh) => {
      spacer.style.height = bar + 'px';
      inner.style.height = bar + 'px';
      logo.style.height = lh + 'px';
    };

    const init = getNavHeights(window.scrollY > 24);
    curBar = init.bar;
    curLogo = init.logo;
    setHeights(curBar, curLogo);

    const animateTo = (targetBar, targetLogo) => {
      if (frameId) cancelAnimationFrame(frameId);
      const fromBar = curBar;
      const fromLogo = curLogo;
      const start = performance.now();
      const duration = 350;

      const tick = (now) => {
        const t = Math.min((now - start) / duration, 1);
        const e = easeOutCubic(t);
        curBar = fromBar + (targetBar - fromBar) * e;
        curLogo = fromLogo + (targetLogo - fromLogo) * e;
        setHeights(curBar, curLogo);
        if (t < 1) {
          frameId = requestAnimationFrame(tick);
        } else {
          frameId = null;
        }
      };
      frameId = requestAnimationFrame(tick);
    };

    let lastShrunk = window.scrollY > 24;
    const onScroll = () => {
      const shrunk = window.scrollY > 24;
      if (shrunk === lastShrunk) return;
      lastShrunk = shrunk;
      const h = getNavHeights(shrunk);
      if (document.documentElement.classList.contains('reduced-motion')) {
        if (frameId) { cancelAnimationFrame(frameId); frameId = null; }
        curBar = h.bar;
        curLogo = h.logo;
        setHeights(curBar, curLogo);
      } else {
        animateTo(h.bar, h.logo);
      }
    };

    const onResize = () => {
      if (frameId) { cancelAnimationFrame(frameId); frameId = null; }
      lastShrunk = window.scrollY > 24;
      const h = getNavHeights(lastShrunk);
      curBar = h.bar;
      curLogo = h.logo;
      setHeights(curBar, curLogo);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, []);

  const scrollToSupport = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
    const el = document.getElementById('support');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', '/#support');
    } else {
      navigate('/#support');
    }
  };

  const toggleDropdown = (dropdownName) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  const isActive = (linkName) => activeLink === linkName;

  const getLinkClasses = (linkName) => {
    const base = 'px-2 xl:px-3 py-2 text-sm xl:text-base font-medium transition-all duration-200 ease-smooth whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-ifys-blue focus:ring-offset-2 nav-link border-b-2';
    return isActive(linkName)
      ? `text-ifys-blue border-ifys-blue ${base}`
      : `text-ifys-gray border-transparent hover:text-ifys-blue hover:border-ifys-blue/50 ${base}`;
  };

  const getMobileLinkClasses = (linkName) => {
    const base = 'block px-3 py-3 min-h-[44px] text-base font-medium flex items-center transition-all duration-200 ease-smooth';
    return isActive(linkName)
      ? `text-ifys-blue bg-gray-50 ${base}`
      : `text-ifys-gray hover:text-ifys-blue hover:bg-gray-50 ${base}`;
  };

  const DROPDOWN_ITEMS = {
    'our-story': [
      { to: '/our-story/ifys-story', label: 'IFYS Story' },
      { to: '/our-story/our-people', label: 'Our People' },
      { to: '/our-story/sanctuary', label: 'Sanctuary' },
    ],
    services: [
      { to: '/services/ifys-services', label: 'IFYS Services' },
      { to: '/services/youth-services', label: 'Youth Services' },
      { to: '/services/child-protection', label: 'Child Protection' },
      { to: '/services/disability-services', label: 'Disability Services' },
      { to: '/services/child-and-family-services', label: 'Child and Family Services' },
      { to: '/services/housing-and-accommodation', label: 'Housing and Accommodation' },
      { to: '/services/social-enterprises', label: 'Social Enterprises' },
    ],
    employment: [
      { to: '/employment/ifys-recruitment', label: 'IFYS Careers' },
      { to: '/employment/current-vacancies', label: 'Current Vacancies' },
      { to: '/employment/volunteers', label: 'Volunteers' },
    ],
  };

  const dropdownItemBase = 'block px-4 py-3 min-h-[44px] text-sm focus:outline-none focus:bg-gray-100 focus:text-ifys-blue flex items-center transition-colors duration-150';
  const mobileDropdownItemBase = 'block px-3 py-3 min-h-[44px] text-sm focus:outline-none focus:text-ifys-blue focus:bg-gray-100 flex items-center transition-colors duration-150';

  const getDropdownItemClass = (to, isLast) => {
    const isCurrent = currentPath === to;
    const stateClass = isCurrent
      ? 'bg-ifys-blue/5 text-ifys-blue font-semibold'
      : 'text-gray-700 hover:bg-gray-50 hover:text-ifys-blue';
    const borderClass = isLast ? '' : 'border-b border-gray-100';
    return `${dropdownItemBase} ${stateClass} ${borderClass}`;
  };

  const getMobileDropdownItemClass = (to) => {
    const isCurrent = currentPath === to;
    const stateClass = isCurrent
      ? 'text-ifys-blue font-semibold'
      : 'text-gray-700 hover:text-ifys-blue';
    return `${mobileDropdownItemBase} ${stateClass}`;
  };

  const DropdownButton = ({ name, label }) => {
    const items = DROPDOWN_ITEMS[name] || [];
    return (
      <div className="relative">
        <button
          onClick={() => toggleDropdown(name)}
          className={getLinkClasses(name)}
          aria-expanded={openDropdown === name}
          aria-haspopup="true"
        >
          {label}
        </button>
        {openDropdown === name && (
          <div className="absolute top-full left-0 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[220px] z-50 overflow-hidden" role="menu">
            {items.map((item, i) => (
              <Link
                key={item.to}
                to={item.to}
                className={getDropdownItemClass(item.to, i === items.length - 1)}
                role="menuitem"
                aria-current={currentPath === item.to ? 'page' : undefined}
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  const MobileDropdownButton = ({ name, label }) => {
    const items = DROPDOWN_ITEMS[name] || [];
    return (
      <div>
        <button
          onClick={() => toggleDropdown(name + '-mobile')}
          className={getMobileLinkClasses(name) + ' w-full text-left'}
          aria-expanded={openDropdown === name + '-mobile'}
          aria-haspopup="true"
        >
          {label}
        </button>
        {openDropdown === name + '-mobile' && (
          <div className="bg-gray-50 pl-4 mt-1 rounded-md" role="menu">
            {items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={getMobileDropdownItemClass(item.to)}
                role="menuitem"
                aria-current={currentPath === item.to ? 'page' : undefined}
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div ref={spacerRef} aria-hidden="true" />

      <nav
        className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm"
        style={{ transition: 'box-shadow 0.3s ease' }}
        aria-label="Main navigation"
      >
        <div className="w-full px-0">
          <div
            ref={navInnerRef}
            className="flex justify-between items-center"
          >
            <Link to="/" className="flex items-center pl-2 sm:pl-4">
              <img
                ref={logoRef}
                src="/images/IFYS Logo.png"
                alt="IFYS - Home"
                className="nav-logo w-auto"
                fetchpriority="high"
                loading="eager"
                decoding="async"
              />
            </Link>

            <div className="hidden lg:block flex-1" ref={dropdownRef}>
              <div className="flex items-baseline justify-center space-x-4 xl:space-x-6">
                <Link to="/" className={getLinkClasses('home')}>Home</Link>
                <DropdownButton name="our-story" label="Our Story" />
                <DropdownButton name="services" label="Services" />
                <Link to="/#support" className={getLinkClasses('support')} onClick={scrollToSupport}>Get Support</Link>
                <DropdownButton name="employment" label="Employment" />
                <Link to="/donations/donations-at-ifys" className={getLinkClasses('donations')}>Donate</Link>
                <Link to="/partnerships/partner-with-us" className={getLinkClasses('partnerships')}>Partnerships</Link>
                <Link to="/contact/contact-ifys" className={getLinkClasses('contact')}>Contact</Link>
              </div>
            </div>

            <div className="lg:hidden pr-2 sm:pr-4">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-ifys-gray hover:text-ifys-blue p-2 min-w-[44px] min-h-[44px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-ifys-blue rounded"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {isMobileMenuOpen && (
            <div className="lg:hidden bg-white border-t border-gray-200">
              <div className="px-4 py-3 space-y-1">
                <Link to="/" className={getMobileLinkClasses('home')}>Home</Link>
                <MobileDropdownButton name="our-story" label="Our Story" />
                <MobileDropdownButton name="services" label="Services" />
                <Link to="/#support" className={getMobileLinkClasses('support')} onClick={scrollToSupport}>Get Support</Link>
                <MobileDropdownButton name="employment" label="Employment" />
                <Link to="/donations/donations-at-ifys" className={getMobileLinkClasses('donations')}>Donate</Link>
                <Link to="/partnerships/partner-with-us" className={getMobileLinkClasses('partnerships')}>Partnerships</Link>
                <Link to="/contact/contact-ifys" className={getMobileLinkClasses('contact')}>Contact</Link>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navbar;
